import { getAccessTokenAsync } from './auth';

// Permission constants
export const PERMISSIONS = {
  LOGIN: 'asm-login-permission',
  SETTINGS: 'asm-settings-permission', 
  MANAGE_REPORTS: 'asm-manage-reports',
  MANAGE_OPERATIONS: 'asm-manage-operations',
  MANAGE_TEAMS: 'asm-manage-teams'
} as const;

export type Permission = typeof PERMISSIONS[keyof typeof PERMISSIONS];

// Interface for user permissions data
interface UserPermissions {
  permissions: string[];
  roles: string[];
  cachedAt: number;
}

// Cache for permissions (5 minutes)
const PERMISSIONS_CACHE_DURATION = 5 * 60 * 1000;
let permissionsCache: UserPermissions | null = null;

/**
 * Get user permissions from JWT token or cache
 */
export const getUserPermissions = async (): Promise<UserPermissions | null> => {
  try {
    // Check cache first
    if (permissionsCache && (Date.now() - permissionsCache.cachedAt) < PERMISSIONS_CACHE_DURATION) {
      console.log('🔐 Using cached permissions');
      return permissionsCache;
    }

    const accessToken = await getAccessTokenAsync();
    if (!accessToken) {
      console.log('❌ No access token available');
      return null;
    }

    // Decode JWT token
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    console.log('🔍 JWT Token Payload:', payload);
    
    // Extract permissions and roles from token
    // Handle both array format and object format from backend
    let permissions: string[] = [];
    if (payload.permissions) {
      if (Array.isArray(payload.permissions)) {
        // Array format: ["asm-login-permission", "asm-settings-permission"]
        permissions = payload.permissions;
      } else if (typeof payload.permissions === 'object') {
        // Object format: { "asm-login-permission": true, "asm-settings-permission": false }
        permissions = Object.keys(payload.permissions).filter(key => payload.permissions[key] === true);
      }
    }
    
    const roles = payload.roles || [];
    
    console.log('🔐 Extracted Permissions from Token:', permissions);
    console.log('🔐 Extracted Roles from Token:', roles);
    
    // If no permissions in token, try to get from localStorage (stored during login)
    if (permissions.length === 0) {
      try {
        const storedPermissions = localStorage.getItem('userPermissions');
        console.log('🔐 Stored permissions in localStorage:', storedPermissions);
        if (storedPermissions) {
          const parsed = JSON.parse(storedPermissions);
          permissions = parsed.permissions || [];
          console.log('🔐 Using stored permissions from localStorage:', permissions);
        } else {
          console.log('🔐 No stored permissions found in localStorage');
        }
      } catch (error) {
        console.error('❌ Error parsing stored permissions:', error);
      }
    }
    
    const userPermissions: UserPermissions = {
      permissions,
      roles,
      cachedAt: Date.now()
    };

    // Cache the permissions
    permissionsCache = userPermissions;
    
    // Also store in localStorage for offline access
    localStorage.setItem('userPermissions', JSON.stringify(userPermissions));
    
    console.log('🔐 User permissions loaded:', { permissions, roles });
    return userPermissions;
    
  } catch (error) {
    console.error('❌ Error getting user permissions:', error);
    
    // Try to get from localStorage as fallback
    try {
      const cached = localStorage.getItem('userPermissions');
      if (cached) {
        const parsed = JSON.parse(cached);
        console.log('🔐 Using offline cached permissions');
        return parsed;
      }
    } catch (e) {
      console.error('❌ Error parsing cached permissions:', e);
    }
    
    return null;
  }
};

/**
 * Check if user has a specific permission
 */
export const hasPermission = async (permission: Permission): Promise<boolean> => {
  try {
    console.log(`🔐 Checking permission: ${permission}`);
    const userPermissions = await getUserPermissions();
    if (!userPermissions) {
      console.log(`❌ No permissions available for check: ${permission}`);
      return false;
    }

    console.log(`🔐 User permissions array:`, userPermissions.permissions);
    const hasIt = userPermissions.permissions.includes(permission);
    console.log(`🔐 Permission check: ${permission} = ${hasIt}`);
    console.log(`🔐 Looking for: "${permission}"`);
    console.log(`🔐 Available permissions:`, userPermissions.permissions.map(p => `"${p}"`));
    return hasIt;
    
  } catch (error) {
    console.error(`❌ Error checking permission ${permission}:`, error);
    return false;
  }
};

/**
 * Check if user has any of the specified permissions
 */
export const hasAnyPermission = async (permissions: Permission[]): Promise<boolean> => {
  try {
    const userPermissions = await getUserPermissions();
    if (!userPermissions) {
      return false;
    }

    return permissions.some(permission => userPermissions.permissions.includes(permission));
    
  } catch (error) {
    console.error('❌ Error checking any permission:', error);
    return false;
  }
};

/**
 * Check if user has all of the specified permissions
 */
export const hasAllPermissions = async (permissions: Permission[]): Promise<boolean> => {
  try {
    const userPermissions = await getUserPermissions();
    if (!userPermissions) {
      return false;
    }

    return permissions.every(permission => userPermissions.permissions.includes(permission));
    
  } catch (error) {
    console.error('❌ Error checking all permissions:', error);
    return false;
  }
};

/**
 * Check if user has a specific role
 */
export const hasRole = async (role: string): Promise<boolean> => {
  try {
    const userPermissions = await getUserPermissions();
    if (!userPermissions) {
      return false;
    }

    return userPermissions.roles.includes(role);
    
  } catch (error) {
    console.error(`❌ Error checking role ${role}:`, error);
    return false;
  }
};

/**
 * Clear permissions cache (useful for logout)
 */
export const clearPermissionsCache = (): void => {
  permissionsCache = null;
  localStorage.removeItem('userPermissions');
  console.log('🧹 Permissions cache cleared');
};

/**
 * Get all user permissions (for debugging)
 */
export const getAllUserPermissions = async (): Promise<{ permissions: string[]; roles: string[] } | null> => {
  const userPermissions = await getUserPermissions();
  if (!userPermissions) {
    return null;
  }
  
  return {
    permissions: userPermissions.permissions,
    roles: userPermissions.roles
  };
};

/**
 * Debug function to show all token details
 */
export const debugTokenDetails = async (): Promise<void> => {
  try {
    const accessToken = await getAccessTokenAsync();
    if (!accessToken) {
      console.log('❌ No access token found');
      return;
    }

    console.log('🔍 Full Access Token:', accessToken);
    
    // Decode token
    const payload = JSON.parse(atob(accessToken.split('.')[1]));
    console.log('🔍 JWT Token Payload:', payload);
    console.log('🔍 Token Expiration:', new Date(payload.exp * 1000).toLocaleString('de-DE'));
    console.log('🔍 Token Valid:', payload.exp > Date.now() / 1000);
    
    // User-Typ explizit loggen
    const userTypeLabels: Record<number, string> = { 1: 'Persönlich', 2: 'Technisch (Shared Device)' };
    const typeLabel = userTypeLabels[payload.typeID] || `Unbekannt (${payload.typeID})`;
    console.log(`👤 User-Typ: typeID=${payload.typeID} → ${typeLabel}`);
    
    // Show permissions from token
    let permissions: string[] = [];
    if (payload.permissions) {
      if (Array.isArray(payload.permissions)) {
        permissions = payload.permissions;
      } else if (typeof payload.permissions === 'object') {
        permissions = Object.keys(payload.permissions).filter(key => payload.permissions[key] === true);
      }
    }
    
    const roles = payload.roles || [];
    
    console.log('🔐 Available Permissions from Token:', permissions);
    console.log('🔐 Available Roles from Token:', roles);
    
    // Check stored permissions from localStorage
    try {
      const storedPermissions = localStorage.getItem('userPermissions');
      if (storedPermissions) {
        const parsed = JSON.parse(storedPermissions);
        console.log('🔐 Stored Permissions from localStorage:', parsed.permissions);
        console.log('🔐 Stored Roles from localStorage:', parsed.roles);
      }
    } catch (error) {
      console.error('❌ Error reading stored permissions:', error);
    }
    
    // Check specific permissions
    console.log('🔐 Has asm-login-permission:', permissions.includes('asm-login-permission'));
    console.log('🔐 Has asm-settings-permission:', permissions.includes('asm-settings-permission'));
    console.log('🔐 Has asm-manage-reports:', permissions.includes('asm-manage-reports'));
    
  } catch (error) {
    console.error('❌ Error debugging token details:', error);
  }
};

/**
 * Check if user can login (has asm-login-permission)
 */
export const canLogin = async (): Promise<boolean> => {
  console.log('🔐 Checking login permission:', PERMISSIONS.LOGIN);
  const result = await hasPermission(PERMISSIONS.LOGIN);
  console.log('🔐 Login permission result:', result);
  return result;
};

/**
 * Check if user can access settings (has asm-settings-permission)
 */
export const canAccessSettings = async (): Promise<boolean> => {
  console.log('🔐 Checking settings permission:', PERMISSIONS.SETTINGS);
  const result = await hasPermission(PERMISSIONS.SETTINGS);
  console.log('🔐 Settings permission result:', result);
  return result;
};

/**
 * Check if user can manage reports (has asm-manage-reports)
 */
export const canManageReports = async (): Promise<boolean> => {
  console.log('🔐 Checking reports permission:', PERMISSIONS.MANAGE_REPORTS);
  const result = await hasPermission(PERMISSIONS.MANAGE_REPORTS);
  console.log('🔐 Reports permission result:', result);
  return result;
};

/**
 * Check if user can manage operations (has asm-manage-operations)
 */
export const canManageOperations = async (): Promise<boolean> => {
  console.log('🔐 Checking operations permission:', PERMISSIONS.MANAGE_OPERATIONS);
  const result = await hasPermission(PERMISSIONS.MANAGE_OPERATIONS);
  console.log('🔐 Operations permission result:', result);
  return result;
};

/**
 * Check if user can manage teams (has asm-manage-teams)
 */
export const canManageTeams = async (): Promise<boolean> => {
  console.log('🔐 Checking teams permission:', PERMISSIONS.MANAGE_TEAMS);
  const result = await hasPermission(PERMISSIONS.MANAGE_TEAMS);
  console.log('🔐 Teams permission result:', result);
  return result;
};
